import { Link, NavLink, Outlet } from "react-router-dom";
import { cn } from "@/lib/cn";
import { TopBar } from "@/components/top-bar";
import { useAuthStore } from "@/stores/auth-store";
import {
  LayoutDashboard, Building2, Users, Briefcase, UserSquare2, ClipboardList,
  Landmark, Rocket, UserCog, Target, CheckSquare, CalendarDays, BarChart3, Inbox,
  Package, Lightbulb, PhoneCall, MessageSquarePlus, Mail, Swords, UserCheck, Gauge,
  FileText, Activity,
} from "lucide-react";

const nav = [
  { to: "/",                  label: "Dashboard",       icon: LayoutDashboard },
  { to: "/call-queue",        label: "Call Queue",      icon: PhoneCall },
  { to: "/leads",             label: "Leads",           icon: Target },
  { to: "/contacts",          label: "Contacts",        icon: Users },
  { to: "/accounts",          label: "Accounts",        icon: Building2 },
  { to: "/deals",             label: "Deals",           icon: Briefcase },
  { to: "/tasks",             label: "Tasks",           icon: CheckSquare },
  { to: "/meetings",          label: "Meetings",        icon: CalendarDays },
  { to: "/my-inbox",          label: "My Inbox",        icon: Mail },
  { to: "/shared-inbox",      label: "Shared Inbox",    icon: Inbox },
  { to: "/documents",         label: "Documents",       icon: FileText },
  { to: "/gov-opportunities", label: "Government",      icon: Landmark },
  { to: "/projects",          label: "Delivery",        icon: Rocket },
  { to: "/products",          label: "IT Products",     icon: Package },
  { to: "/solutions",         label: "IT Solutions",    icon: Lightbulb },
  { to: "/competitors",       label: "Competitors",     icon: Swords },
  { to: "/reports",           label: "Reports",         icon: BarChart3 },
  { to: "/dashboards",        label: "Dashboards",      icon: Gauge },
  { to: "/feedback",          label: "Feedback / IT",   icon: MessageSquarePlus },
  { to: "/admin",             label: "Admin",           icon: UserCog },
  { to: "/admin/users",       label: "User Admin",      icon: Users },
];

const phase2Nav = [
  { to: "/my-requests", label: "My Requests", icon: Inbox },
  { to: "/job-orders",  label: "Job Orders",  icon: ClipboardList },
  { to: "/candidates",  label: "Candidates",  icon: UserSquare2 },
  { to: "/placements",  label: "Placements",  icon: UserCheck },
];

// Management-only views.
const MANAGER_ROLES = ["SUPER_ADMIN", "ADMIN", "LEADERSHIP", "SALES_MANAGER"];
const managerNav = [
  { to: "/rep-activity", label: "Rep Activity", icon: Activity },
];

export function AppShell() {
  const me = useAuthStore((s) => s.me);
  const isManager = MANAGER_ROLES.includes(me?.role ?? "");
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="flex w-56 flex-col border-r bg-card">
        <div className="flex h-14 items-center border-b px-4">
          <Link to="/" className="text-base font-semibold tracking-tight">
            CBC CRM
          </Link>
        </div>
        <nav className="flex flex-col flex-1 overflow-y-auto p-2">
          <div className="flex-1 space-y-1">
            {nav.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
            {isManager && managerNav.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </div>
          <div className="mt-4 border-t pt-3">
            <p className="px-3 pb-1 text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
              Phase 2
            </p>
            <div className="space-y-1">
              {phase2Nav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                      isActive ? "bg-primary/10 text-primary" : "text-foreground/60 hover:bg-muted hover:text-foreground",
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>
      </aside>

      <div className="flex flex-1 flex-col min-w-0">
        <TopBar />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
